'use strict';

class PsNewArrivals {
  constructor(element) {
    this.el           = element;
    this.heading      = element.dataset.heading      || 'Nye kort på lager';
    this.subheading   = element.dataset.subheading   || '';
    this.interval     = (parseInt(element.dataset.interval, 10) || 4) * 1000;
    this.limit        = parseInt(element.dataset.productLimit, 10) || 20;

    this.products     = [];
    this.currentIndex = 0;
    this.autoTimer    = null;
    this.isHovered    = false;
    this.isTouching   = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this._onScroll  = this._syncFromScroll.bind(this);
    this._onKeydown = this._handleKeydown.bind(this);

    this.init();
  }

  async init() {
    this.el.classList.add('ps-new-arrivals');
    this.el.innerHTML = '<p class="ps-new-arrivals__status">Laster kort…</p>';
    try {
      await this.fetchProducts();
    } catch (err) {
      console.error('[PsNewArrivals] fetch failed:', err);
      this.el.innerHTML = '<p class="ps-new-arrivals__status">Feil: kunne ikke hente kort (' + err.message + ')</p>';
      return;
    }
    if (!this.products.length) {
      console.warn('[PsNewArrivals] no available products returned');
      this.el.innerHTML = '<p class="ps-new-arrivals__status">Ingen kort på lager akkurat nå.</p>';
      return;
    }
    console.log('[PsNewArrivals] rendering', this.products.length, 'products');
    this.render();
    this.bindEvents();
    if (!this.reducedMotion) this.startAutoAdvance();
  }

  async fetchProducts() {
    const res = await fetch('/products.json?limit=50&sort_by=updated-descending');
    if (!res.ok) throw new Error('fetch failed');
    const { products } = await res.json();

    // Sort client-side as a fallback — Shopify AJAX may not honour sort_by
    products.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));

    this.products = products
      .filter(p => (p.variants || []).some(v => v.available))
      .slice(0, this.limit);
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  _stockCount(product) {
    const total = (product.variants || []).reduce(
      (sum, v) => sum + Math.max(0, v.inventory_quantity || 0),
      0
    );
    return total > 0 ? total : ((product.variants || []).some(v => v.available) ? 1 : 0);
  }

  _formatPrice(priceStr) {
    const n = parseFloat(priceStr);
    if (isNaN(n)) return priceStr;
    // Shopify /products.json returns price as a decimal string (e.g. "299.00")
    return Math.round(n) + ' kr';
  }

  _imageUrl(product) {
    const fi = product.featured_image;
    if (fi) return typeof fi === 'string' ? fi : (fi.src || '');
    const imgs = product.images || [];
    if (!imgs.length) return '';
    const first = imgs[0];
    return typeof first === 'string' ? first : (first.src || '');
  }

  _esc(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  render() {
    const heading = this.heading
      ? `<h2 class="ps-new-arrivals__heading">${this._esc(this.heading)}</h2>` : '';
    const subheading = this.subheading
      ? `<p class="ps-new-arrivals__subheading">${this._esc(this.subheading)}</p>` : '';

    const cards = this.products.map(p => this._renderCard(p)).join('');

    const dots = this.products.map((_, i) =>
      `<button class="ps-new-arrivals__dot${i === 0 ? ' ps-new-arrivals__dot--active' : ''}"
               aria-label="Gå til kort ${i + 1}"
               data-index="${i}"></button>`
    ).join('');

    this.el.innerHTML = `
      <div class="ps-new-arrivals__header">${heading}${subheading}</div>
      <div class="ps-new-arrivals__carousel-wrap">
        <button class="ps-new-arrivals__arrow ps-new-arrivals__arrow--prev"
                aria-label="Forrige" type="button">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
               stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M15 18l-6-6 6-6"/>
          </svg>
        </button>
        <div class="ps-new-arrivals__track"
             role="region"
             aria-roledescription="carousel"
             aria-label="${this._esc(this.heading)}"
             tabindex="0">
          ${cards}
        </div>
        <button class="ps-new-arrivals__arrow ps-new-arrivals__arrow--next"
                aria-label="Neste" type="button">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
               stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </button>
      </div>
      <div class="ps-new-arrivals__dots">${dots}</div>
    `;

    this.track   = this.el.querySelector('.ps-new-arrivals__track');
    this.prevBtn = this.el.querySelector('.ps-new-arrivals__arrow--prev');
    this.nextBtn = this.el.querySelector('.ps-new-arrivals__arrow--next');
    this.dotEls  = Array.from(this.el.querySelectorAll('.ps-new-arrivals__dot'));
  }

  _renderCard(product) {
    const stock = this._stockCount(product);
    const price = this._formatPrice(product.variants?.[0]?.price || '0');
    const image = this._imageUrl(product);

    const imgHtml = image
      ? `<img class="ps-new-arrivals__card-image"
              src="${this._esc(image)}"
              alt="${this._esc(product.title)}"
              loading="lazy" width="400" height="560">`
      : `<div class="ps-new-arrivals__card-image ps-new-arrivals__card-image--placeholder"
              role="img" aria-label="${this._esc(product.title)}"></div>`;

    return `
      <div class="ps-new-arrivals__card product-card grid__item"
           data-product-id="${product.id}"
           data-product-handle="${this._esc(product.handle)}"
           data-product-title="${this._esc(product.title)}">
        <a class="ps-new-arrivals__card-link"
           href="/products/${this._esc(product.handle)}"
           aria-label="${this._esc(product.title)}">
          <div class="ps-new-arrivals__card-image-wrap">
            ${imgHtml}
            <span class="ps-new-arrivals__card-badge" aria-hidden="true">Nytt!</span>
          </div>
          <div class="ps-new-arrivals__card-info">
            <h3 class="ps-new-arrivals__card-title">${this._esc(product.title)}</h3>
            <p class="ps-new-arrivals__card-price">${price}</p>
          </div>
        </a>
        <div class="ps-new-arrivals__card-wishlist-slot"
             data-wishlist-king-product-id="${product.id}"
             data-wishlist-king-product-handle="${this._esc(product.handle)}"></div>
      </div>
    `;
  }

  // ── Events ─────────────────────────────────────────────────────────────────

  bindEvents() {
    this.prevBtn.addEventListener('click', () => { this.advance(-1); this._resetAuto(); });
    this.nextBtn.addEventListener('click', () => { this.advance(1);  this._resetAuto(); });

    this.dotEls.forEach(dot => {
      dot.addEventListener('click', () => {
        this.goTo(parseInt(dot.dataset.index, 10));
        this._resetAuto();
      });
    });

    // Pause on hover
    this.el.addEventListener('mouseenter', () => {
      this.isHovered = true;
      this.stopAutoAdvance();
    });
    this.el.addEventListener('mouseleave', () => {
      this.isHovered = false;
      if (!this.reducedMotion) this.startAutoAdvance();
    });

    // Pause on touch
    this.track.addEventListener('touchstart', () => {
      this.isTouching = true;
      this.stopAutoAdvance();
    }, { passive: true });
    this.track.addEventListener('touchend', () => {
      this.isTouching = false;
      if (!this.reducedMotion) this.startAutoAdvance();
    }, { passive: true });

    // Sync dots on manual scroll
    this.track.addEventListener('scroll', this._onScroll, { passive: true });

    // Keyboard navigation when track is focused
    this.track.addEventListener('keydown', this._onKeydown);
  }

  // ── Navigation ─────────────────────────────────────────────────────────────

  advance(dir) {
    const total = this.products.length;
    this.goTo((this.currentIndex + dir + total) % total);
  }

  goTo(index) {
    this.currentIndex = index;
    const card = this.track.children[index];
    if (!card) return;
    this.track.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
    this._syncDots(index);
  }

  _syncFromScroll() {
    const cards = this.track.children;
    if (!cards.length) return;
    const scrollLeft = this.track.scrollLeft;
    let closest = 0;
    let minDist = Infinity;
    for (let i = 0; i < cards.length; i++) {
      const dist = Math.abs(cards[i].offsetLeft - scrollLeft);
      if (dist < minDist) { minDist = dist; closest = i; }
    }
    if (closest !== this.currentIndex) {
      this.currentIndex = closest;
      this._syncDots(closest);
    }
  }

  _syncDots(index) {
    this.dotEls.forEach((dot, i) =>
      dot.classList.toggle('ps-new-arrivals__dot--active', i === index)
    );
  }

  _handleKeydown(e) {
    if (e.key === 'ArrowLeft')  { this.advance(-1); this._resetAuto(); e.preventDefault(); }
    if (e.key === 'ArrowRight') { this.advance(1);  this._resetAuto(); e.preventDefault(); }
  }

  // ── Auto-advance ───────────────────────────────────────────────────────────

  startAutoAdvance() {
    if (this.autoTimer || this.reducedMotion) return;
    this.autoTimer = setInterval(() => {
      if (!this.isHovered && !this.isTouching) this.advance(1);
    }, this.interval);
  }

  stopAutoAdvance() {
    clearInterval(this.autoTimer);
    this.autoTimer = null;
  }

  _resetAuto() {
    this.stopAutoAdvance();
    if (!this.reducedMotion) this.startAutoAdvance();
  }

  // ── Cleanup ────────────────────────────────────────────────────────────────

  destroy() {
    this.stopAutoAdvance();
    this.track?.removeEventListener('scroll',  this._onScroll);
    this.track?.removeEventListener('keydown', this._onKeydown);
    this.el.innerHTML = '';
  }
}

function _psNaMount(root) {
  (root || document).querySelectorAll('[data-ps-new-arrivals]:not([data-ps-na-mounted])').forEach(el => {
    el.setAttribute('data-ps-na-mounted', '1');
    new PsNewArrivals(el);
  });
}

// Normal page load — handle both early and late script execution
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => _psNaMount());
} else {
  _psNaMount();
}

// Shopify Theme Customizer — sections are injected dynamically after load
document.addEventListener('shopify:section:load', e => _psNaMount(e.target));
